// Admin "Settings" — admin console screen 5 of 5, the LAST screen, route
// /settings (deliberately not "/admin/settings" — see App.jsx's own
// route-table comment and components/AdminNav.jsx's header comment: any path
// starting with "/admin" collides with the dev proxy's/Nginx's API path
// prefix for a real browser navigation, not just fetch()).
//
// One global settings object, not a list of rows — a single form, not a
// table (this task's brief). Exact field list: company name, TIN, VAT
// percent, quote validity days, default delivery days, default warranty
// months, three notification-channel DEFAULTS (email/Telegram/WhatsApp —
// labelled as defaults because `organizations` can override per-org; this
// screen only sets the global fallback), and the Excel-import fuzzy-match
// confidence floor. See hooks/useAdminSettings.js and
// services/settingsService.js's own comments for the two real
// settings-table keys deliberately NOT shown here (signature/stamp storage
// paths, cron alert-timing knobs) — this screen cannot accidentally leak
// them because GET /admin/settings never returns them at all.
//
// No cost price or margin anywhere on this screen (root CLAUDE.md rule #1) —
// nothing here reads product_costs; the settings table has no cost concept.
// No scale/meter/position indicator either, so the list_price/floor_price
// domain rule does not apply to this screen.
//
// Single "Save changes" action covering everything that changed, matching
// how one coherent "settings" concept usually reads (this task's own brief
// leaves the choice open; the PUT endpoint accepts a partial object either
// way). Only the fields that actually differ from the last-saved values are
// sent, mirroring updateSettingsSchema's partial-update semantics and
// settingsService.js's own "before/after only the changed keys" audit
// shape — this component's diffing is not just an optimization, it is what
// keeps the audit_log entry accurate.
//
// Saved-flash reuses WorkspacePage.jsx's own savedFlash pattern (root
// CLAUDE.md's explicit instruction for this screen) rather than inventing a
// new success affordance.

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useAdminSettings, useUpdateAdminSettings } from '../hooks/useAdminSettings.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import TwoFactorSettings from '../components/TwoFactorSettings.jsx';
import BrandAssetUpload from '../components/BrandAssetUpload.jsx';
import { useBrandAssets } from '../hooks/useBrandAssets.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './SettingsPage.module.css';

// kind decides three things per field: how a server value becomes a
// controlled-input value, how that input value becomes an API value, and
// how it is compared to the last-saved value to decide whether it belongs
// in the PUT patch at all.
const FIELD_DEFS = [
  { key: 'companyName', kind: 'text' },
  { key: 'companyTin', kind: 'text' },
  { key: 'companyTagline', kind: 'text' },
  { key: 'companyAddress', kind: 'text' },
  { key: 'companyPhone', kind: 'text' },
  { key: 'companyEmail', kind: 'text' },
  { key: 'companyOpeningHours', kind: 'text' },
  { key: 'commercialRegistrationNumber', kind: 'text' },
  { key: 'vatPercent', kind: 'number' },
  { key: 'quoteValidityDays', kind: 'number' },
  { key: 'defaultDeliveryDays', kind: 'number' },
  { key: 'defaultWarrantyMonths', kind: 'number' },
  { key: 'defaultPaymentTerms', kind: 'text' },
  { key: 'notifyEmailDefault', kind: 'boolean' },
  { key: 'notifyTelegramDefault', kind: 'boolean' },
  { key: 'notifyWhatsappDefault', kind: 'boolean' },
  { key: 'matchConfidenceFloor', kind: 'number' },
  { key: 'proformaShowQr', kind: 'boolean' },
  // --- Security-hardening task settings (migration 012) -------------------
  { key: 'loginAttemptCap', kind: 'number' },
  { key: 'loginLockoutMinutes', kind: 'number' },
  { key: 'sessionIdleHours', kind: 'number' },
  { key: 'sessionAbsoluteHours', kind: 'number' },
  { key: 'trackingLinkGraceDays', kind: 'number' },
];

function toLocalValue(kind, serverValue) {
  if (kind === 'text') return serverValue ?? '';
  if (kind === 'number')
    return serverValue === null || serverValue === undefined ? '' : String(serverValue);
  return Boolean(serverValue);
}

function toApiValue(kind, localValue) {
  if (kind === 'text') return localValue.trim();
  if (kind === 'number') return Number(localValue);
  return Boolean(localValue);
}

function isUnchanged(kind, localValue, serverValue) {
  if (kind === 'text') return localValue.trim() === (serverValue ?? '');
  if (kind === 'number') return Number(localValue) === Number(serverValue);
  return Boolean(localValue) === Boolean(serverValue);
}

function buildLocalState(settings) {
  const state = {};
  FIELD_DEFS.forEach(({ key, kind }) => {
    state[key] = toLocalValue(kind, settings[key]);
  });
  return state;
}

// Per-field max length + "too long" error id — mirrors each text field's
// own z.string().max(...) in routes/adminSettingsRoutes.js's
// updateSettingsSchema exactly.
const TEXT_FIELD_LIMITS = {
  companyName: { max: 200, errorId: 'settings.error.companyNameTooLong' },
  companyTin: { max: 20, errorId: 'settings.error.companyTinTooLong' },
  companyTagline: { max: 200, errorId: 'settings.error.companyTaglineTooLong' },
  companyAddress: { max: 255, errorId: 'settings.error.companyAddressTooLong' },
  companyPhone: { max: 30, errorId: 'settings.error.companyPhoneTooLong' },
  companyEmail: { max: 190, errorId: 'settings.error.companyEmailTooLong' },
  companyOpeningHours: { max: 120, errorId: 'settings.error.companyOpeningHoursTooLong' },
  commercialRegistrationNumber: {
    max: 40,
    errorId: 'settings.error.commercialRegistrationNumberTooLong',
  },
  defaultPaymentTerms: { max: 255, errorId: 'settings.error.defaultPaymentTermsTooLong' },
};

// Client-side mirror of routes/adminSettingsRoutes.js's updateSettingsSchema
// — fast feedback only, the server re-validates every field regardless
// (this is not the primary validation layer, same relationship every other
// admin/ form's client validate() has to its server-side zod schema).
function validateField(key, kind, localValue) {
  if (kind === 'text') {
    const { max, errorId } = TEXT_FIELD_LIMITS[key];
    return localValue.trim().length > max ? errorId : null;
  }
  if (kind === 'boolean') return null;

  // kind === 'number'
  const n = Number(localValue);
  if (localValue === '' || Number.isNaN(n)) {
    return rangeErrorId(key);
  }
  switch (key) {
    case 'vatPercent':
      return n < 0 || n > 100 ? rangeErrorId(key) : null;
    case 'quoteValidityDays':
      return !Number.isInteger(n) || n < 1 || n > 365 ? rangeErrorId(key) : null;
    case 'defaultDeliveryDays':
      return !Number.isInteger(n) || n < 0 || n > 365 ? rangeErrorId(key) : null;
    case 'defaultWarrantyMonths':
      return !Number.isInteger(n) || n < 0 || n > 120 ? rangeErrorId(key) : null;
    case 'matchConfidenceFloor':
      return n < 0 || n > 1 ? rangeErrorId(key) : null;
    case 'loginAttemptCap':
      return !Number.isInteger(n) || n < 1 || n > 20 ? rangeErrorId(key) : null;
    case 'loginLockoutMinutes':
      return !Number.isInteger(n) || n < 1 || n > 1440 ? rangeErrorId(key) : null;
    case 'sessionIdleHours':
      return !Number.isInteger(n) || n < 1 || n > 168 ? rangeErrorId(key) : null;
    case 'sessionAbsoluteHours':
      return !Number.isInteger(n) || n < 1 || n > 720 ? rangeErrorId(key) : null;
    case 'trackingLinkGraceDays':
      return !Number.isInteger(n) || n < 0 || n > 365 ? rangeErrorId(key) : null;
    default:
      return null;
  }
}

function rangeErrorId(key) {
  const idByKey = {
    vatPercent: 'settings.error.vatPercentRange',
    quoteValidityDays: 'settings.error.quoteValidityDaysRange',
    defaultDeliveryDays: 'settings.error.defaultDeliveryDaysRange',
    defaultWarrantyMonths: 'settings.error.defaultWarrantyMonthsRange',
    matchConfidenceFloor: 'settings.error.matchConfidenceFloorRange',
    loginAttemptCap: 'settings.error.loginAttemptCapRange',
    loginLockoutMinutes: 'settings.error.loginLockoutMinutesRange',
    sessionIdleHours: 'settings.error.sessionIdleHoursRange',
    sessionAbsoluteHours: 'settings.error.sessionAbsoluteHoursRange',
    trackingLinkGraceDays: 'settings.error.trackingLinkGraceDaysRange',
  };
  return idByKey[key];
}

export default function SettingsPage() {
  const { t } = useLanguage();
  const settingsQuery = useAdminSettings();
  const updateMutation = useUpdateAdminSettings();
  const brandAssetsQuery = useBrandAssets();

  const { toastOk, toastErr } = useActionFeedback();
  const [localValues, setLocalValues] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const lastSavedRef = useRef(null);

  // Initialize local form state once the settings object first loads, and
  // again any time a fresh server object arrives that this component itself
  // did not just produce via a successful save (lastSavedRef tracks that).
  useEffect(() => {
    if (settingsQuery.data && settingsQuery.data !== lastSavedRef.current) {
      setLocalValues(buildLocalState(settingsQuery.data));
      lastSavedRef.current = settingsQuery.data;
    }
  }, [settingsQuery.data]);

  const setField = (key) => (value) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges =
    localValues &&
    lastSavedRef.current &&
    FIELD_DEFS.some(
      ({ key, kind }) => !isUnchanged(kind, localValues[key], lastSavedRef.current[key])
    );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!localValues || !lastSavedRef.current) return;

    const errors = {};
    FIELD_DEFS.forEach(({ key, kind }) => {
      const message = validateField(key, kind, localValues[key]);
      if (message) errors[key] = message;
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const patch = {};
    FIELD_DEFS.forEach(({ key, kind }) => {
      if (!isUnchanged(kind, localValues[key], lastSavedRef.current[key])) {
        patch[key] = toApiValue(kind, localValues[key]);
      }
    });
    if (Object.keys(patch).length === 0) return;

    updateMutation.mutate(patch, {
      onSuccess: (data) => {
        setLocalValues(buildLocalState(data.settings));
        lastSavedRef.current = data.settings;
        setFieldErrors({});
        toastOk(t('settings.savedToast'));
      },
      onError: (err) => toastErr(err, ['VALIDATION_ERROR']),
    });
  };

  const renderTextField = (key) => (
    <label className={styles.field} key={key}>
      {t(`settings.field.${key}.label`)}
      <input
        type="text"
        className={styles.input}
        value={localValues[key]}
        maxLength={TEXT_FIELD_LIMITS[key].max}
        onChange={(e) => setField(key)(e.target.value)}
      />
      {fieldErrors[key] && <span className={styles.fieldError}>{t(fieldErrors[key])}</span>}
    </label>
  );

  const renderNumberField = (key, { min, max, step }) => (
    <label className={styles.field} key={key}>
      {t(`settings.field.${key}.label`)}
      <input
        type="number"
        className={`${styles.input} tabularNums`}
        value={localValues[key]}
        min={min}
        max={max}
        step={step}
        onChange={(e) => setField(key)(e.target.value)}
      />
      {fieldErrors[key] && <span className={styles.fieldError}>{t(fieldErrors[key])}</span>}
    </label>
  );

  const renderCheckboxField = (key) => (
    <label className={styles.checkboxField} key={key}>
      <input
        type="checkbox"
        checked={localValues[key]}
        onChange={(e) => setField(key)(e.target.checked)}
      />
      {t(`settings.field.${key}.label`)}
    </label>
  );

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('settings.title')}</h1>
      <p className={styles.subtitle}>{t('settings.subtitle')}</p>

      {settingsQuery.isLoading && <LoadingState variant="rows" rows={9} />}

      {settingsQuery.isError && !settingsQuery.isLoading && (
        <ErrorState
          messageId="settings.errorBody"
          error={settingsQuery.error}
          onRetry={settingsQuery.refetch}
        />
      )}

      {!settingsQuery.isLoading && !settingsQuery.isError && localValues && (
        // noValidate: same reason as LoginPage.jsx's own form — the number
        // inputs' native min/max constraints would otherwise pop up the
        // browser's own validation bubble, which is never translated and
        // would show untranslated English even in Amharic mode (root
        // CLAUDE.md: "No hardcoded English in a component. Ever.").
        // validateField() below renders the same range check through
        // shared/i18n instead.
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.company')}</h2>
            <div className={styles.grid2}>
              {renderTextField('companyName')}
              {renderTextField('companyTin')}
              {renderTextField('companyTagline')}
              {renderTextField('commercialRegistrationNumber')}
              {renderTextField('companyAddress')}
              {renderTextField('companyPhone')}
              {renderTextField('companyEmail')}
              {renderTextField('companyOpeningHours')}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.quotationDefaults')}</h2>
            <div className={styles.grid2}>
              {renderNumberField('vatPercent', { min: 0, max: 100, step: 0.01 })}
              {renderNumberField('quoteValidityDays', { min: 1, max: 365, step: 1 })}
              {renderNumberField('defaultDeliveryDays', { min: 0, max: 365, step: 1 })}
              {renderNumberField('defaultWarrantyMonths', { min: 0, max: 120, step: 1 })}
              {renderTextField('defaultPaymentTerms')}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.notificationDefaults')}</h2>
            <p className={styles.sectionHint}>{t('settings.notificationDefaults.hint')}</p>
            <div className={styles.checkboxRow}>
              {renderCheckboxField('notifyEmailDefault')}
              {renderCheckboxField('notifyTelegramDefault')}
              {renderCheckboxField('notifyWhatsappDefault')}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.importMatching')}</h2>
            <p className={styles.sectionHint}>{t('settings.field.matchConfidenceFloor.hint')}</p>
            <div className={styles.grid2}>
              {renderNumberField('matchConfidenceFloor', { min: 0, max: 1, step: 0.01 })}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.verification')}</h2>
            <p className={styles.sectionHint}>{t('settings.field.proformaShowQr.hint')}</p>
            <div className={styles.checkboxRow}>{renderCheckboxField('proformaShowQr')}</div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.section.security')}</h2>
            <div className={styles.grid2}>
              {renderNumberField('loginAttemptCap', { min: 1, max: 20, step: 1 })}
              {renderNumberField('loginLockoutMinutes', { min: 1, max: 1440, step: 1 })}
              {renderNumberField('sessionIdleHours', { min: 1, max: 168, step: 1 })}
              {renderNumberField('sessionAbsoluteHours', { min: 1, max: 720, step: 1 })}
              {renderNumberField('trackingLinkGraceDays', { min: 0, max: 365, step: 1 })}
            </div>
          </section>

          <div className={styles.footer}>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={updateMutation.isPending || !hasChanges}
            >
              {updateMutation.isPending && <Spinner />}{' '}
              {updateMutation.isPending ? t('settings.saving') : t('settings.saveButton')}
            </button>
          </div>
        </form>
      )}

      {/* Proforma branding — signature + stamp images. Not part of the
          settings-object form above: the bytes have their own upload flow
          (BrandAssetUpload) and are admin-only + private (CLAUDE.md rule
          #7). Same "outside the form" placement as the 2FA section below. */}
      {!settingsQuery.isLoading && !settingsQuery.isError && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.section.branding')}</h2>
          <p className={styles.sectionHint}>{t('settings.branding.hint')}</p>

          {brandAssetsQuery.isLoading && <LoadingState variant="rows" rows={3} />}
          {brandAssetsQuery.isError && !brandAssetsQuery.isLoading && (
            <ErrorState
              messageId="settings.branding.errorBody"
              error={brandAssetsQuery.error}
              onRetry={brandAssetsQuery.refetch}
            />
          )}
          {!brandAssetsQuery.isLoading && !brandAssetsQuery.isError && brandAssetsQuery.data && (
            <div className={styles.brandingRow}>
              <BrandAssetUpload kind="signature" asset={brandAssetsQuery.data.signature} />
              <BrandAssetUpload kind="stamp" asset={brandAssetsQuery.data.stamp} />
            </div>
          )}
        </section>
      )}

      {/* Not part of the settings-object form above — 2FA lives on `users`,
          not the `settings` table, and owns its own save flow (see
          components/TwoFactorSettings.jsx's own header comment). Always
          rendered once the page itself is past its loading/error states,
          independent of settingsQuery. */}
      {!settingsQuery.isLoading && !settingsQuery.isError && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.section.twoFactor')}</h2>
          <p className={styles.sectionHint}>{t('settings.twoFactor.hint')}</p>
          <TwoFactorSettings />
        </section>
      )}
    </div>
  );
}
